import { yupResolver } from "@hookform/resolvers/yup";
import { api } from "constants/RotasAPI";
import { usePostCriarProcessoJudicial } from "hooks/processosJudiciais/usePostCriarProcessoJudicial";
import { ReactNode } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import CustomButton from "shared/components/button/CustomButton";
import { CheckBox } from "shared/components/checkbox/CheckBox";
import TextInput from "shared/components/input/TextInput";
import CustomModal from "shared/components/modal/CustomModal";
import CustomMultiSelect from "shared/components/select/CustomMultiSelectAsync";
import SelectAsync from "shared/components/select/SelectAsync";
import { ProcessosJudiciaisSchema } from "shared/yup/schemas/ProcessosJudiciaisSchema";
import { RegistroAcordoDTO } from "types/dto/RegistroAcordoDTO";
import { RegistroCondominioDTO } from "types/dto/RegistroCondominioDTO";
import { RegistroUnidadeDTO } from "types/dto/RegistroUnidadeDTO";
import { SelectOptionType } from "types/select/SelectOptionType";
import { RequestCriarProcessoJudicialDTO } from "types/request/RequestCriarProcessoJudicial";
import showAlert from "shared/util/functions/alert/showAlert";
import { queryClient } from "shared/util/queryClient";

interface ModalProcessosJudiciaisProps {
  showModal: boolean;
  onCloseModal: () => void;
}

interface ProcessosJudiciaisForm {
  condominio: SelectOptionType<RegistroCondominioDTO> | null;
  unidade: SelectOptionType<RegistroUnidadeDTO> | null;
  numeroProcesso: string | null;
  possuiAcordosVinculados: boolean;
  acordos: (SelectOptionType<RegistroAcordoDTO> | undefined)[] | undefined;
}

const ModalProcessosJudiciais: React.FC<ModalProcessosJudiciaisProps> = (props) => {
  const { register, handleSubmit, reset, control, formState, watch, setValue, clearErrors, setError } =
    useForm<ProcessosJudiciaisForm>({
      resolver: yupResolver<ProcessosJudiciaisForm>(ProcessosJudiciaisSchema),
    });

  const { mutateAsync: postProcessoJudicial } = usePostCriarProcessoJudicial();

  const renderBodyModalProcessosJuridicos = () => {
    return (
      <Container>
        <Row>
          <Col xl={4} sm={4} xs={4}>
            {renderSelecionarCondominio()}
          </Col>
          <Col xl={4} sm={4} xs={4}>
            {renderSelecionarUnidade()}
          </Col>
          <Col xl={4} sm={4} xs={4}>
            {renderSelecionarNumeroProcesso()}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>{renderPossuiAcordosVinculados()}</Col>
          <Col xl={8} sm={8} xs={8}>
            {renderSelecionarAcordos()}
          </Col>
        </Row>
      </Container>
    );
  };

  const renderSelecionarCondominio = () => {
    return (
      <SelectAsync
        usarValidacaoParaFormulario
        id="selecionar-condominio"
        label="Condomínio"
        name="condominio"
        required={true}
        endpoint={api.condominio.getRegistrosCondominios}
        onChange={(opcaoSelecionada) => {
          if (watch("unidade")) {
            setValue("unidade", null);
          }
        }}
        control={control}
        register={register}
        setValue={setValue}
        nomeAtributoValue="id"
        nomeAtributoLabel="nomeFantasia"
      />
    );
  };

  const renderSelecionarUnidade = () => {
    return (
      <SelectAsync
        usarValidacaoParaFormulario
        id="unidade"
        name="unidade"
        label="Unidade"
        nomeAtributoValue="id"
        nomeAtributoLabel="blocoNumero"
        required
        onChange={() => {
          if (watch("acordos")) {
            setValue("acordos", undefined);
          }
        }}
        endpoint={`${api.unidade.getRegistrosUnidades}/${watch("condominio")?.value}`}
        atributosRefresh={[watch("condominio")?.value]}
        isDisabled={!watch("condominio")?.value}
        control={control}
        register={register}
        setValue={setValue}
      />
    );
  };

  const renderSelecionarNumeroProcesso = () => {
    return (
      <TextInput
        id="numeroProcesso"
        name="numeroProcesso"
        label="Número do processo"
        required
        usarValidacaoParaFormulario
        setValue={setValue}
        control={control}
        register={register}
      />
    );
  };

  const renderPossuiAcordosVinculados = () => {
    return (
      <div className="mt-4">
        <CheckBox
          id="cadastrar-proximo"
          usarValidacaoParaFormulario
          name="possuiAcordosVinculados"
          control={control}
          register={register}
          setValue={setValue}
          onChange={(isChecked) => {
            if (!isChecked) {
              clearErrors("acordos");
              setValue("acordos", undefined);
            }
          }}
        />
        <span>Processo possui acordos vinculados?</span>
      </div>
    );
  };

  const renderSelecionarAcordos = () => {
    return (
      <CustomMultiSelect
        id="acordos"
        name="acordos"
        required
        register={register}
        key={"acordos"}
        control={control}
        setValue={setValue}
        endpoint={`${api.acordo.getRegistroAcordoByUnidadeId}/${watch("unidade")?.value}`}
        nomeAtributoLabel={`numeroAcordo`}
        customNomeAtributoLabelPrefix={`Acordo `}
        nomeAtributoValue={"id"}
        atributosRefresh={[watch("unidade")?.value]}
        tooltip="Os acordos vinculados no processo serão exibidos no histórico do processo."
        label="Acordos"
        isDisabled={!watch("possuiAcordosVinculados") || !watch("unidade")?.value}
        isClearable
        usarValidacaoParaFormulario
        onChange={(opcao) => {}}
      />
    );
  };

  const renderFooterModalProcessosJudiciais = (): ReactNode => {
    return (
      <Row>
        <Col>
          <CustomButton
            id="btn-cancelar-modal-processos-judiciais"
            label="Cancelar"
            onClick={() => closeModal()}
            variant="danger-alt"
            className="me-2"
          />
        </Col>
        <Col>
          <CustomButton
            id="btn-avancar-modal-processos-judiciais"
            label="Avançar"
            onClick={() => {
              handleSubmit(criarProcessoJudicial)();
            }}
            variant="primary"
          />
        </Col>
      </Row>
    );
  };

  const fecharModal = () => {
    reset();
    props.onCloseModal();
  };
  const closeModal = () => {
    if (formState.isDirty) {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      reset();
      props.onCloseModal();
    }
  };

  const criarProcessoJudicial = async (form: ProcessosJudiciaisForm) => {
    const processosJudicialDTO = {
      unidadeId: form.unidade?.value,
      numeroProcesso: form.numeroProcesso,
      acordosIds: form.acordos?.map((acordo) => acordo?.value),
      condominioId: form.condominio?.value,
    } as RequestCriarProcessoJudicialDTO;
    try {
      const response = await postProcessoJudicial(processosJudicialDTO);
      if (response?.status === 201) {
        reset();
        queryClient.invalidateQueries(["processos-judiciais-historicos"]);
        props.onCloseModal();
      }
    } catch (error: any) {
      if (error?.response?.data?.validation[0]?.groupCode === "processoJudicialExistente") {
        setError("numeroProcesso", {
          type: "manual",
          message: error?.response?.data?.validation[0]?.message,
        });
      }
      if (error?.response?.data?.validation[0]?.groupCode === "unidadeSemAcordo") {
        setError("unidade", {
          type: "manual",
          message: error?.response?.data?.validation[0]?.message,
        });
      }
    }
  };

  return (
    <CustomModal
      titulo="Novo processo"
      onCloseModal={() => closeModal()}
      size="xl"
      tipoModal="MODAL_CENTRAL"
      showModal={props.showModal}
      customFooter={renderFooterModalProcessosJudiciais()}
      fecharModalAoClicarFora={!formState.isDirty}
    >
      {renderBodyModalProcessosJuridicos()}
    </CustomModal>
  );
};

export default ModalProcessosJudiciais;
